import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Container } from "../ui/Container";
import { ExternalButton } from "../ui/Button";
import { Logo } from "../ui/Logo";
import { colors, media, ease } from "../../theme";

const NAV = [
  { label: "Invest", to: "/invest" },
  { label: "Borrow", to: "/borrow" },
  { label: "Products", to: "/products" },
  { label: "Software & ICT", to: "/software" },
  { label: "About", to: "/about" },
];

export const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const lockedScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Robust body scroll lock that works on iOS too.
  // Note: no cleanup function on this effect — cleanup runs before the close
  // branch, which would wipe the styles and prevent scroll restoration.
  useEffect(() => {
    const body = document.body;
    if (open) {
      lockedScrollY.current = window.scrollY;
      body.style.position = "fixed";
      body.style.top = `-${lockedScrollY.current}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
    } else if (body.style.position === "fixed") {
      const y = lockedScrollY.current;
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      // Wait for the browser to reflow before restoring scroll, otherwise
      // the document height isn't yet large enough and the scroll gets clamped.
      // Use 'instant' so the global scroll-behavior: smooth doesn't animate
      // (which leaves the user mid-scroll instead of where they were).
      requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: "instant" }));
    }
  }, [open]);

  // Restore body styles on unmount (separate effect so the per-render lock
  // toggle above doesn't clobber its own state).
  useEffect(() => () => {
    const body = document.body;
    if (body.style.position === "fixed") {
      const y = lockedScrollY.current;
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      window.scrollTo(0, y);
    }
  }, []);

  return (
    <>
      <Bar $scrolled={scrolled}>
        <Container>
          <Inner>
            <Link to="/" aria-label="Everpesa home"><Logo /></Link>

            <Nav>
              {NAV.map((item) => (
                <NavItem key={item.to} to={item.to} end>
                  {item.label}
                </NavItem>
              ))}
            </Nav>

            <Actions>
              <ExternalButton
                variant="ghost"
                size="sm"
                href="https://invest.everpesa.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sign in
              </ExternalButton>
              <ExternalButton
                variant="primary"
                size="sm"
                href="https://invest.everpesa.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open account
              </ExternalButton>
            </Actions>

            <Burger
              aria-label="Open menu"
              aria-expanded={open}
              onClick={() => setOpen(true)}
            >
              <span />
              <span />
              <span />
            </Burger>
          </Inner>
        </Container>
      </Bar>

      <MobileSheet $open={open} role="dialog" aria-modal="true" aria-label="Site navigation">
        <SheetHeader>
          <Container>
            <SheetHeaderInner>
              <Link to="/" aria-label="Everpesa home" onClick={() => setOpen(false)}>
                <Logo />
              </Link>
              <CloseBtn aria-label="Close menu" onClick={() => setOpen(false)}>
                <span />
                <span />
              </CloseBtn>
            </SheetHeaderInner>
          </Container>
        </SheetHeader>

        <SheetBody>
          <Container>
            <MobileNav>
              {NAV.map((item) => (
                <MobileItem
                  key={item.to}
                  to={item.to}
                  end
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </MobileItem>
              ))}
            </MobileNav>
            <MobileActions>
              <ExternalButton
                variant="outline"
                href="https://invest.everpesa.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sign in
              </ExternalButton>
              <ExternalButton
                variant="primary"
                href="https://invest.everpesa.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open account
              </ExternalButton>
            </MobileActions>
          </Container>
        </SheetBody>
      </MobileSheet>
    </>
  );
};

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${(p) => (p.$scrolled ? "rgba(255,255,255,0.85)" : "transparent")};
  backdrop-filter: ${(p) => (p.$scrolled ? "saturate(180%) blur(12px)" : "none")};
  -webkit-backdrop-filter: ${(p) => (p.$scrolled ? "saturate(180%) blur(12px)" : "none")};
  border-bottom: 1px solid ${(p) => (p.$scrolled ? colors.line : "transparent")};
  transition: all 250ms ${ease};
`;

const Inner = styled.div`
  height: 76px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 32px;

  ${media.lg} { grid-template-columns: auto auto; }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: center;
  gap: 8px;
  ${media.lg} { display: none; }
`;

const itemBase = css`
  position: relative;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  color: ${colors.ink};
  transition: background 200ms ${ease}, color 200ms ${ease};

  &:hover { background: ${colors.brandSoft}; color: ${colors.brand}; }

  &.active {
    color: ${colors.brand};
    background: ${colors.brandSoft};
  }
`;

const NavItem = styled(NavLink)`${itemBase}`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
  ${media.lg} { display: none; }
`;

const Burger = styled.button`
  display: none;
  ${media.lg} {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${colors.surfaceAlt};
    border: 1px solid ${colors.line};
    justify-self: end;
    padding: 0 12px;
  }

  span {
    display: block;
    height: 2px;
    background: ${colors.ink};
    border-radius: 2px;
  }
`;

/* ── Mobile sheet: full-viewport modal with its own header ── */

const MobileSheet = styled.div`
  position: fixed;
  inset: 0;
  background: ${colors.surface};
  z-index: 200;
  display: none;
  flex-direction: column;
  transform: translateY(${(p) => (p.$open ? "0" : "-100%")});
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? "auto" : "none")};
  transition: transform 320ms ${ease}, opacity 220ms ${ease};
  /* Respect iOS safe area on notch devices */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);

  ${media.lg} { display: flex; }
`;

const SheetHeader = styled.div`
  flex: 0 0 auto;
  border-bottom: 1px solid ${colors.line};
`;

const SheetHeaderInner = styled.div`
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseBtn = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${colors.surfaceAlt};
  border: 1px solid ${colors.line};
  cursor: pointer;
  flex: 0 0 auto;

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 18px;
    height: 2px;
    background: ${colors.ink};
    border-radius: 2px;
    transform-origin: center;
  }
  span:nth-child(1) { transform: translate(-50%, -50%) rotate(45deg); }
  span:nth-child(2) { transform: translate(-50%, -50%) rotate(-45deg); }
`;

const SheetBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  padding: 28px 0 32px;

  & > div {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 28px;
  }
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
`;

const MobileItem = styled(NavLink)`
  padding: 18px 4px;
  font-size: 22px;
  font-weight: 600;
  color: ${colors.ink};
  border-bottom: 1px solid ${colors.line};
  font-family: 'Sora', sans-serif;

  &.active { color: ${colors.brand}; }
`;

const MobileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
`;
